import {createApp, h, defineAsyncComponent} from 'vue';
import {createPinia} from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import VueCookies from 'vue-cookies';
import router from '@/router';
import type {VerifyTranslations} from '@/features/auth-verify/types';

const AuthVerifyModal = defineAsyncComponent(() => import('@/features/auth-verify/AuthVerifyModal.vue'));

/**
 *  Imperative entrypoint for legacy jQuery widgets that need to obtain a verification token
 *  before posting a destructive action. Spins up a one-shot Vue app rooted in a transient
 *  container; resolves with the token string on success, or null if the user closes the
 *  modal. The widget keeps ownership of the destructive request itself.
 */
export function openDeleteCompanyVerify(
    companyId: number | string,
    translations: VerifyTranslations
): Promise<string | null> {
    return new Promise((resolve) => {
        const container = document.createElement('div');
        document.body.appendChild(container);

        let settled = false;
        const settle = (token: string | null) => {
            if (settled) return;
            settled = true;
            try { app.unmount(); } catch { /* nothing to clean up */ }
            container.remove();
            resolve(token);
        };

        const app = createApp({
            name: 'DeleteCompanyVerifyHost',
            render() {
                return h(AuthVerifyModal, {
                    translations,
                    purpose: 'delete-company',
                    resourceId: String(companyId),
                    titleKey: 'lbl-auth-verify-deletecompany-title',
                    onVerified: (token: string | null) => settle(token),
                    onClose: () => settle(null),
                });
            }
        });

        const pinia = createPinia();
        pinia.use(piniaPluginPersistedstate);
        app.use(pinia);
        app.use(router);
        app.use(VueCookies);
        app.mount(container);
    });
}
